import { SPACES } from '@lumapps/communities/keys';
import { VisibilityOptionType } from '@lumapps/communities/types';
import { LegacyContentStatus } from '@lumapps/contents/types';
import { googleCalendar, membersOnlyContribution } from '@lumapps/lumx/custom-icons';
import {
    mdiCalendar,
    mdiFileDocumentOutline,
    mdiFolderNetworkOutline,
    mdiGoogleDrive,
    mdiMicrosoft,
    mdiMicrosoftOutlook,
    mdiHomeOutline,
    mdiMessageTextOutline,
    mdiNewspaper,
    mdiNewspaperVariantMultipleOutline,
    mdiCalendarMonthOutline,
    mdiSchoolOutline,
    mdiEarth,
    mdiEyeOff,
    mdiLock,
    mdiLeadPencil,
    mdiCalendarClock,
} from '@lumapps/lumx/icons';
import { ColorPalette } from '@lumapps/lumx/react';
import { GLOBAL, type TranslateKey } from '@lumapps/translations';
import { ONE_MINUTE } from '@lumapps/utils/time/constants';
import { type ManagedStreamResources } from '@lumapps/widget-stream/types';

import { SPACE_KEYS } from './keys';
import { SPACE_DRAFT_IDENTIFIER, SPACE_SCHEDULED_IDENTIFIER } from './routes';
import {
    SpaceLegacyPrivacy,
    SpaceServiceProvider,
    SpaceServices,
    SpaceViews,
    SpaceVisibility,
    SpaceWebviews,
} from './types';

/**
 * Map from legacy communities privacy/visibility to space visibility
 */
export const spaceVisibilityToLegacyPrivacyMap: Record<SpaceVisibility, SpaceLegacyPrivacy> = {
    [SpaceVisibility.open]: SpaceLegacyPrivacy.open,
    [SpaceVisibility.read_only]: SpaceLegacyPrivacy.readOnly,
    [SpaceVisibility.private]: SpaceLegacyPrivacy.descriptionOnly,
    [SpaceVisibility.secret]: SpaceLegacyPrivacy.restricted,
};

export const SPACE_VISIBILITY_ICONS: Record<SpaceLegacyPrivacy, string> = {
    [SpaceLegacyPrivacy.open]: mdiEarth,
    [SpaceLegacyPrivacy.readOnly]: membersOnlyContribution,
    [SpaceLegacyPrivacy.descriptionOnly]: mdiLock,
    [SpaceLegacyPrivacy.restricted]: mdiEyeOff,
};

export const SPACE_VISIBILITIES: Array<VisibilityOptionType<SpaceLegacyPrivacy>> = [
    {
        // "open" visibility
        id: SpaceLegacyPrivacy.open,
        title: SPACES.PRIVACY_OPEN,
        description: SPACES.PRIVACY_OPEN_DESCRIPTION,
        icon: SPACE_VISIBILITY_ICONS[SpaceLegacyPrivacy.open],
    },
    {
        // "members-only contribution" visibility
        id: SpaceLegacyPrivacy.readOnly,
        title: SPACES.PRIVACY_READ_ONLY,
        description: SPACES.PRIVACY_READ_ONLY_DESCRIPTION,
        icon: SPACE_VISIBILITY_ICONS[SpaceLegacyPrivacy.readOnly],
    },
    {
        // "private" visibility
        id: SpaceLegacyPrivacy.descriptionOnly,
        title: SPACES.PRIVACY_DESCRIPTION_ONLY,
        description: SPACES.PRIVACY_DESCRIPTION_ONLY_DESCRIPTION,
        icon: SPACE_VISIBILITY_ICONS[SpaceLegacyPrivacy.descriptionOnly],
    },
    {
        // "secret" visibility
        id: SpaceLegacyPrivacy.restricted,
        title: SPACES.PRIVACY_RESTRICTED,
        description: SPACES.PRIVACY_RESTRICTED_DESCRIPTION,
        icon: SPACE_VISIBILITY_ICONS[SpaceLegacyPrivacy.restricted],
    },
];

/**
 * List of all the Space services associated pages to be shown in the Space navigation.
 */
export const SPACE_SERVICES_RECORD: Record<
    keyof typeof SpaceServices,
    { icon: string; label: TranslateKey; view: SpaceViews }
> = {
    [SpaceServices.homepage]: {
        icon: mdiHomeOutline,
        label: GLOBAL.HOME,
        view: SpaceViews.home,
    },
    [SpaceServices.feed]: {
        icon: mdiNewspaperVariantMultipleOutline,
        label: SPACES.FEED,
        view: SpaceViews.feed,
    },
    [SpaceServices.posts]: {
        icon: mdiMessageTextOutline,
        label: GLOBAL.POSTS,
        view: SpaceViews.posts,
    },
    [SpaceServices.article]: {
        icon: mdiNewspaper,
        label: GLOBAL.ARTICLES,
        view: SpaceViews.articles,
    },
    [SpaceServices.event]: {
        icon: mdiCalendar,
        label: GLOBAL.EVENTS,
        view: SpaceViews.events,
    },
    [SpaceServices.learning]: {
        icon: mdiSchoolOutline,
        label: SPACES.LEARNING,
        view: SpaceViews.learning,
    },
    [SpaceServices.documents]: {
        icon: mdiFileDocumentOutline,
        label: SPACES.MANAGED_MEDIA_LIBRARY,
        view: SpaceViews.documents,
    },
    [SpaceServices.drive]: {
        icon: mdiFolderNetworkOutline,
        label: SPACES.MEDIA,
        view: SpaceViews.drive,
    },
    [SpaceServices.calendar]: {
        icon: mdiCalendarMonthOutline,
        label: GLOBAL.CALENDAR,
        view: SpaceViews.calendar,
    },
};

export const SPACE_SERVICES_ALT_ICONS = {
    [SpaceServices.drive]: {
        [SpaceServiceProvider.google]: mdiGoogleDrive,
        [SpaceServiceProvider.microsoft]: mdiMicrosoft,
    },
    [SpaceServices.calendar]: {
        [SpaceServiceProvider.google]: googleCalendar,
        [SpaceServiceProvider.microsoft]: mdiMicrosoftOutlook,
    },
};

export const SPACE_PUBLICATION_STATUS_RECORD: Record<
    string,
    {
        identifier: string;
        labelTranslateKey: TranslateKey;
        emptyStateTitleTranslateKey: TranslateKey;
        emptyStateMessageTranslateKey: TranslateKey;
        sortedByTranslateKey: TranslateKey;
        icon: string;
        statusColor: ColorPalette;
        sortOrder?: string;
    }
> = {
    [LegacyContentStatus.draft]: {
        identifier: SPACE_DRAFT_IDENTIFIER,
        labelTranslateKey: GLOBAL.DRAFT,
        emptyStateTitleTranslateKey: SPACE_KEYS.EMPTY_DRAFT_POST_LIST_TITLE,
        emptyStateMessageTranslateKey: SPACE_KEYS.EMPTY_DRAFT_POST_LIST_MESSAGE,
        sortedByTranslateKey: GLOBAL.LAST_UPDATE,
        icon: mdiLeadPencil,
        statusColor: ColorPalette.yellow,
        sortOrder: '-updatedAt',
    },
    [LegacyContentStatus.pending]: {
        identifier: SPACE_SCHEDULED_IDENTIFIER,
        labelTranslateKey: GLOBAL.SCHEDULED,
        emptyStateTitleTranslateKey: SPACE_KEYS.EMPTY_SCHEDULED_POST_LIST_TITLE,
        emptyStateMessageTranslateKey: SPACE_KEYS.EMPTY_SCHEDULED_POST_LIST_MESSAGE,
        sortedByTranslateKey: GLOBAL.PUBLICATION_DATE,
        icon: mdiCalendarClock,
        statusColor: ColorPalette.green,
        sortOrder: 'startDate',
    },
};

/** Array containing all the possible Resources types for a space feed. */
export const SPACE_FEED_RESOURCE_TYPES: ManagedStreamResources[] = ['post', 'article', 'event'];

/** Space views that should be displayed using webview */
export const WEBVIEW_COMPATIBLE_SPACE_SERVICES = [
    SpaceWebviews.calendar,
    SpaceWebviews.homepage,
    SpaceWebviews.settings,
];

/** Social alpha feature flag */
export const SOCIAL_ALPHA_FF = 'social-alpha';

/** Content in space feature flag */
export const CONTENT_IN_SPACE_FF = 'content-in-space';

/**
 * Default React Query cache configuration for space-related queries.
 * Provides optimal caching strategy for space feeds, post lists, and pinned posts.
 */
export const SPACE_DEFAULT_CACHE_CONFIG = {
    staleTime: 10 * ONE_MINUTE, // 10 minutes - data is considered fresh
    gcTime: 30 * ONE_MINUTE, // 30 minutes - keep in cache before garbage collection
    refetchOnMount: true, // Refetch on mount, display cached data while fetching new data
    refetchOnWindowFocus: true, // Refetch when window regains focus, to ensure up-to-date data
} as const;

/**
 * SessionStorage key used to track published space content for showing "add to navigation" notification.
 * Set when content is published in a space context (in Angular designer or React writer mode).
 */
export const SPACE_CONTENT_CREATED_SESSION_STORAGE_KEY = 'spaceContentCreatedId';
